import { useEffect, useState } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db, ensureAnonAuth } from '@/lib/firebase';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Edit, Plus, MapPin, Droplet, ChevronDown, ChevronRight, Trash2, Download } from 'lucide-react';
import { Event, PondingLog } from './types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface EventsListProps {
  onEdit?: (event: Event) => void;
  onAddPonding?: (eventId: string) => void;
  onEditPonding?: (ponding: PondingLog) => void;
}

export default function EventsList({ onEdit, onAddPonding, onEditPonding }: EventsListProps) {
  const [events, setEvents] = useState<Event[]>([]);
  const [pondingLogs, setPondingLogs] = useState<Record<string, PondingLog[]>>({});
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'event' | 'ponding', id: string, eventId?: string } | null>(null);

  const toggleEventExpanded = (eventId: string) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await deleteDoc(doc(db, 'events', eventId));
      setEvents(prev => prev.filter(e => e.id !== eventId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete event', err);
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleDeletePonding = async (pondingId: string, eventId: string) => {
    try {
      await deleteDoc(doc(db, 'ponding-logs', pondingId));
      setPondingLogs(prev => ({
        ...prev,
        [eventId]: prev[eventId]?.filter(p => p.id !== pondingId) || []
      }));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete ponding log', err);
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleExportEventCSV = (event: Event) => {
    const eventPondings = pondingLogs[event.id] || [];
    
    // CSV headers
    const headers = [
      'Event Title',
      'Event Description',
      'Reporter',
      'Latitude',
      'Longitude',
      'Region',
      'City',
      'Country',
      'Date',
      'Depth (mm)',
      'Source',
      'Link'
    ];
    
    // Convert ponding logs to CSV rows
    const rows = eventPondings.map(ponding => {
      const date = ponding.date instanceof Date 
        ? ponding.date 
        : ponding.date?.toDate?.();
      const dateString = date?.toISOString() || '';
      
      return [
        event.title,
        event.description,
        ponding.reporter,
        ponding.latitude,
        ponding.longitude,
        ponding.region,
        ponding.city,
        ponding.country,
        dateString,
        ponding.depthMm,
        ponding.source || '',
        ponding.link || ''
      ].map(value => {
        // Escape values containing commas, quotes, or newlines
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });
    });
    
    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${event.title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        // Ensure user is authenticated before fetching data
        await ensureAnonAuth();
        
        // Load events
        const eventsSnap = await getDocs(collection(db, 'events'));
        if (!mounted) return;
        
        const eventsData = eventsSnap.docs.map(d => ({ 
          id: d.id, 
          ...d.data() 
        })) as Event[];
        
        setEvents(eventsData);

        // Load all ponding logs
        const pondingsSnap = await getDocs(collection(db, 'ponding-logs'));
        if (!mounted) return;
        
        const allPondings = pondingsSnap.docs.map(d => ({ 
          id: d.id, 
          ...d.data() 
        })) as PondingLog[];

        // Group pondings by eventId
        const pondingsByEvent: Record<string, PondingLog[]> = {};
        allPondings.forEach(ponding => {
          if (!pondingsByEvent[ponding.eventId]) {
            pondingsByEvent[ponding.eventId] = [];
          }
          pondingsByEvent[ponding.eventId].push(ponding);
        });

        setPondingLogs(pondingsByEvent);
        
      } catch (err) {
        console.error('Failed to load events', err);
        if (mounted) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, []);

  if (error) {
    return (
      <Card className="border-destructive bg-destructive/10 py-2">
        <CardContent className="p-3 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
          <span className="text-xs text-destructive">Error loading events: {error}</span>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="p-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-1 flex-1">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-60" />
                </div>
              </div>
              <Skeleton className="h-16 w-full" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <Card className="py-2">
        <CardContent className="p-3">
          <div className="text-center py-4">
            <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-50" />
            <p className="text-xs text-muted-foreground">No events found. Create your first flood event!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm?.type === 'event' 
                ? 'This will permanently delete this flood event and all associated ponding logs. This action cannot be undone.'
                : 'This will permanently delete this ponding log. This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteConfirm?.type === 'event') {
                  handleDeleteEvent(deleteConfirm.id);
                } else if (deleteConfirm?.type === 'ponding' && deleteConfirm.eventId) {
                  handleDeletePonding(deleteConfirm.id, deleteConfirm.eventId);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="space-y-2">
        {events.map(event => {
          const eventPondings = pondingLogs[event.id] || [];
          const isExpanded = expandedEvents.has(event.id);
          
          return (
            <Card key={event.id} className="overflow-hidden">
              <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/30">
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-sm font-semibold">{event.title}</h3>
                    <span className="text-xs text-muted-foreground">• {event.description}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 hover:bg-accent"
                    onClick={() => handleExportEventCSV(event)}
                    title="Export as CSV"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 hover:bg-accent"
                    onClick={() => onEdit?.(event)}
                    title="Edit event"
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 hover:bg-destructive hover:text-destructive-foreground"
                    onClick={() => setDeleteConfirm({ type: 'event', id: event.id })}
                    title="Delete event"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              <CardContent className="px-3 pb-2 pt-2">
                <div className="flex items-center justify-between mb-1.5">
                  <button
                    onClick={() => toggleEventExpanded(event.id)}
                    className="flex items-center gap-1 hover:bg-accent rounded px-1.5 py-0.5 transition-colors"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    <h4 className="text-xs font-medium text-muted-foreground">
                      Ponding Logs ({eventPondings.length})
                    </h4>
                  </button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1.5 text-xs"
                    onClick={() => onAddPonding?.(event.id)}
                  >
                    <Plus className="h-3 w-3" />
                    Add Ponding
                  </Button>
                </div>

                {isExpanded && (
                  eventPondings.length === 0 ? (
                    <div className="text-center py-3 border rounded-lg bg-muted/20">
                      <Droplet className="h-6 w-6 text-muted-foreground mx-auto mb-1 opacity-50" />
                      <p className="text-xs text-muted-foreground">
                        No ponding logs yet
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {eventPondings.map(ponding => (
                        <div 
                          key={ponding.id} 
                          className="border rounded-md bg-card/50 hover:bg-card/80 transition-colors p-2 text-xs group"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap flex-1">
                            <div className="flex items-center gap-1.5">
                              <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                              <span className="font-medium">{ponding.city}, {ponding.region}</span>
                            </div>
                            <Badge variant="secondary" className="gap-0.5 text-xs h-5">
                              <Droplet className="h-2.5 w-2.5" />
                              {ponding.depthMm}mm
                            </Badge>
                            <span className="text-muted-foreground">
                              {ponding.latitude}, {ponding.longitude}
                            </span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-muted-foreground">
                              {ponding.reporter}
                            </span>
                            <span className="text-muted-foreground">•</span>
                            <span className="text-muted-foreground">
                              {(() => {
                                const date = ponding.date instanceof Date 
                                  ? ponding.date 
                                  : ponding.date?.toDate?.();
                                return date?.toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                }) || 'Unknown date';
                              })()}
                            </span>
                            {(ponding.streetViewImages?.length || ponding.eventViewImages?.length) ? (
                              <>
                                <span className="text-muted-foreground">•</span>
                                <span className="text-muted-foreground">
                                  📸 {(ponding.streetViewImages?.length || 0) + (ponding.eventViewImages?.length || 0)}
                                </span>
                              </>
                            ) : null}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 hover:bg-accent opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditPonding?.(ponding);
                                }}
                                title="Edit ponding log"
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 hover:bg-destructive hover:text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirm({ type: 'ponding', id: ponding.id, eventId: event.id });
                                }}
                                title="Delete ponding log"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
